import { call, put, select } from 'redux-saga/effects';
import { ApplicationsBySeason } from '../actions/applicationTreatments/types/state';
import {
  fetchingApplicationTreatments,
  receiveApplicationTreatments,
} from '../actions/applicationTreatments/actions';
import { getApplicationEventAggregates } from '../queries/applicationEventAggregates';
import {
  adaptUnifiedEventsToTreatments,
  UnifiedApplicationTreatmentEvent,
} from '../utils/applicationEventAdapter';
import { SeasonContext, SeasonListItem } from '../types/Season';
import { ApplicationTreatment } from '../types/ApplicationTreatment';
import { getSeasonContexts, getSeasonsList } from '../selectors/seasonSelectors';
import { getFieldUuid } from '../selectors/fieldSelectorsNew';
import { requestUnifiedApplicationEvents } from '../requesters/applicationEventAggregatesRequester';
import { mergeSeasonContexts, selectApplicationCardEnabled } from './applicationTreatment/utils';
import { getApplicationAnalyticsQuery } from '../queries/applicationAnalyticsQuery';
import { olConnectionV2 } from '../requesters/olConnection';

function* fetchLegacyTreatments(fieldUUID: string) {
  const olResult = yield call(olConnectionV2, getApplicationAnalyticsQuery(fieldUUID));
  const eventAggregates =
    olResult.data.field.applicationAnalytics.applicationEventAggregates;

  const applicationDataBySeason: ApplicationsBySeason = {};
  eventAggregates.forEach(
    (aggregate: { seasonCode: string; applicationData: ApplicationTreatment[] }) => {
      applicationDataBySeason[aggregate.seasonCode] = aggregate.applicationData;
    },
  );

  yield put(receiveApplicationTreatments(applicationDataBySeason));

  return applicationDataBySeason;
}

export function* getApplicationTreatments(fieldUUID: string, seasonTags: string[] = []) {
  yield put(fetchingApplicationTreatments());

  const applicationCardEnabled: boolean = yield select(selectApplicationCardEnabled);
  if (!applicationCardEnabled) {
    return yield* fetchLegacyTreatments(fieldUUID);
  }

  const seasonContextsFromState: SeasonContext[] = yield select(getSeasonContexts);
  const seasonsList: Record<string, SeasonListItem> = yield select(getSeasonsList);
  const seasonContexts = mergeSeasonContexts(
    seasonContextsFromState,
    seasonTags,
    seasonsList,
  );

  const fieldUuid: string = yield select(getFieldUuid);
  const resolvedFieldId = fieldUuid || fieldUUID;

  if (!seasonContexts.length || !resolvedFieldId) {
    return yield* fetchLegacyTreatments(fieldUUID);
  }

  const contextLookup = seasonContexts.reduce<Record<string, SeasonContext>>((lookup, context) => {
    const key = `${context.year}:${context.seasonName}`;
    lookup[key] = context;
    return lookup;
  }, {});

  const events: UnifiedApplicationTreatmentEvent[] = yield call(
    requestUnifiedApplicationEvents,
    getApplicationEventAggregates({ fieldId: resolvedFieldId }),
  );

  const eventsBySeasonTag = events.reduce<Record<string, UnifiedApplicationTreatmentEvent[]>>(
    (grouped, event) => {
      const context = contextLookup[`${event.seasonYear}:${event.seasonName}`];
      const bucket = grouped[context.seasonTag] ?? (grouped[context.seasonTag] = []);
      bucket.push(event);
      return grouped;
    },
    {},
  );

  const applicationsBySeason = Object.fromEntries(
    Object.entries(eventsBySeasonTag).map(([seasonTag, seasonEvents]) => [
      seasonTag,
      adaptUnifiedEventsToTreatments(seasonEvents, seasonTag),
    ]),
  ) as ApplicationsBySeason;

  yield put(receiveApplicationTreatments(applicationsBySeason));

  return applicationsBySeason;
}
